import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../lib/api.js';
import { useWorkspaceStore } from '../stores/workspace.js';
import type { Stage } from '@mayksel/types';
import type { CreateStageInput, UpdateStageInput } from '@mayksel/validators';

function useApiOpts() {
  const { getToken } = useAuth();
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  return { getToken, workspaceId };
}

export function useStages() {
  const { getToken, workspaceId } = useApiOpts();

  return useQuery<Stage[]>({
    queryKey: ['stages', workspaceId],
    queryFn: async () => {
      const token = await getToken();
      return api.get<Stage[]>('/v1/stages', {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    enabled: !!workspaceId,
  });
}

export function useCreateStage() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateStageInput) => {
      const token = await getToken();
      return api.post<Stage>('/v1/stages', data, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['stages', workspaceId] });
    },
  });
}

export function useUpdateStage(id: string) {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateStageInput) => {
      const token = await getToken();
      return api.patch<Stage>(`/v1/stages/${id}`, data, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['stages', workspaceId] });
    },
  });
}

export function useDeleteStage() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      return api.delete<{ id: string }>(`/v1/stages/${id}`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['stages', workspaceId] });
    },
  });
}

export function useReorderStages() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const token = await getToken();
      return api.post<Stage[]>('/v1/stages/reorder', { ids }, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['stages', workspaceId] });
    },
  });
}
